import { useMutation, useQueryClient, MutationOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { postNewReport } from 'Services';
import { RQ_ALL_REPORTS, RQ_SINGLE_REPORT } from 'Utils';
import { useTranslation } from 'react-i18next';
import useReplaceMerchant from 'Hooks/useReplaceMerchant';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';

type Props = {
    type: ReportType;
    extendedFormValues?: ProcessedReportFormValues;
    mutationOptions?: Omit<MutationOptions<AxiosResponse<any>, any, any, any>, 'mutationKey'>;
};

function useCreateSingleReport({ type, extendedFormValues, mutationOptions }: Props) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { addAlert } = useAlertBanner();
    const contentName = useReplaceMerchant(extendedFormValues?.name || '');

    const values = {
        type,
        properties: {
            ...extendedFormValues
        }
    };

    const defaultSuccessHandler = () => {
        queryClient.invalidateQueries([RQ_ALL_REPORTS, type]);
        const content = t('save report success msg', { name: contentName });
        addAlert({
            id: 'save-report-success',
            content,
            status: 'success'
        });
    };

    return useMutation([RQ_SINGLE_REPORT, values], () => postNewReport(values), {
        ...mutationOptions,
        onSuccess: (data, variables, context) => {
            defaultSuccessHandler();
            mutationOptions?.onSuccess?.(data, variables, context);
            switch (type) {
                case 'merchantsummary': {
                    trackEvent({
                        eventType: EventTypes.track,
                        eventName: EventNames.click,
                        payload: {
                            clickIdentifier: 'New Merchant Summary Report - Save To Report List',
                            text: 'Save New Merchant Summary Report'
                        }
                    });
                    break;
                }
                case 'performance': {
                    trackEvent({
                        eventType: EventTypes.track,
                        eventName: EventNames.click,
                        payload: {
                            clickIdentifier: 'New Rule Performance Report - Save To Report List',
                            text: 'Save New Rule Performance Report'
                        }
                    });
                    break;
                }
                default: {
                    break;
                }
            }
        }
    });
}

export default useCreateSingleReport;
